import { z } from 'zod';

import { buildApiUrl, parseErrorResponse } from '../../apiRequest';

// Keyset (cursor) pagination contract shared by API adapters, hooks, PaginationBar and
// GenericTable. The server (Api::V4::PaginationMethods) returns the page position in response
// headers and keeps each endpoint's existing body root. A cursor is an opaque, encrypted
// token: pass it back unchanged and never build, decode or log one.

const MAX_CURSOR_LENGTH = 8192;

export type Cursor = string & { readonly __brand: 'PaginationCursor' };

interface FirstCursorBoundary {
  kind: 'first';
}

interface AfterCursorBoundary {
  kind: 'after';
  cursor: Cursor;
}

interface BeforeCursorBoundary {
  kind: 'before';
  cursor: Cursor;
}

export type CursorBoundary = FirstCursorBoundary | AfterCursorBoundary | BeforeCursorBoundary;

// `approximate` and `capped` totals are never used to compute pages; `omitted` shows no total.
export type Total =
  | { kind: 'exact'; value: number }
  | { kind: 'approximate'; value: number; asOf: string }
  | { kind: 'capped'; value: number; asOf: string }
  | { kind: 'omitted' };

export interface CursorPage<T> {
  items: T[];
  nextCursor: Cursor | null;
  previousCursor: Cursor | null;
  hasNext: boolean;
  hasPrevious: boolean;
  total: Total;
}

export interface CursorPageRequest<F> {
  filters: F;
  limit: number;
  boundary: CursorBoundary;
}

export type CursorPageMeta = Omit<CursorPage<never>, 'items'>;

export const FIRST_CURSOR_BOUNDARY: CursorBoundary = Object.freeze({ kind: 'first' });

export const OMITTED_TOTAL: Total = Object.freeze({ kind: 'omitted' });

export const cursorSchema = z
  .string()
  .min(1)
  .max(MAX_CURSOR_LENGTH)
  .transform((value) => value as Cursor);

const cursorBoundarySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('first') }),
  z.object({ kind: z.literal('after'), cursor: cursorSchema }),
  z.object({ kind: z.literal('before'), cursor: cursorSchema })
]);

const booleanHeaderSchema = z.enum(['true', 'false']).transform((value) => value === 'true');
const countHeaderSchema = z
  .string()
  .regex(/^\d+$/)
  .transform((value) => Number(value));

const totalKindSchema = z.enum(['exact', 'approximate', 'capped', 'omitted']).catch('omitted');

const cursorHeadersSchema = z.object({
  mode: z.literal('cursor'),
  nextCursor: cursorSchema.nullable(),
  previousCursor: cursorSchema.nullable(),
  hasNext: booleanHeaderSchema,
  hasPrevious: booleanHeaderSchema,
  totalKind: totalKindSchema
});

const countedTotalSchema = z.object({ value: countHeaderSchema });
const datedTotalSchema = z.object({ value: countHeaderSchema, asOf: z.string().min(1) });

// A fetch `Headers` (or anything else with `get`), or RecruitApiClient's `resHead` record,
// whose keys fetch has already lower-cased.
interface HeaderGetter {
  get: (name: string) => string | null | undefined;
}

type HeaderSource = HeaderGetter | Record<string, string | undefined>;

const hasGetter = (headers: HeaderSource): headers is HeaderGetter =>
  typeof (headers as Partial<HeaderGetter>).get === 'function';

const readHeader = (headers: HeaderSource, name: string): string | null => {
  if (hasGetter(headers)) {
    return headers.get(name) ?? null;
  }
  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === name);
  return entry?.[1] ?? null;
};

const parseTotal = (headers: HeaderSource, kind: Total['kind']): Total => {
  const asOf = readHeader(headers, 'x-total-as-of');
  if (kind === 'exact') {
    const parsed = countedTotalSchema.safeParse({ value: readHeader(headers, 'x-total-count') });
    return parsed.success ? { kind, value: parsed.data.value } : OMITTED_TOTAL;
  }
  if (kind === 'approximate' || kind === 'capped') {
    const valueHeader = kind === 'approximate' ? 'x-estimated-total-count' : 'x-total-lower-bound';
    const parsed = datedTotalSchema.safeParse({ value: readHeader(headers, valueHeader), asOf });
    return parsed.success
      ? { kind, value: parsed.data.value, asOf: parsed.data.asOf }
      : OMITTED_TOTAL;
  }
  return OMITTED_TOTAL;
};

export const numberedPageTotals = (
  headers: HeaderSource
): { total: Total; totalPages: number } | null => {
  const kind = readHeader(headers, 'x-total-count-kind');
  if (kind === null) return null;

  const total = parseTotal(headers, totalKindSchema.parse(kind));
  const page = Math.max(1, countHeaderSchema.catch(1).parse(readHeader(headers, 'x-current-page')));
  const hasNext = /^[1-9]\d*$/.test(readHeader(headers, 'x-next-page') ?? '');
  const totalPages =
    total.kind === 'exact'
      ? countHeaderSchema.catch(page).parse(readHeader(headers, 'x-total-pages'))
      : page + (hasNext ? 1 : 0);
  return { total, totalPages };
};

// Throws when the response is not a cursor page, so a caller never mistakes a numbered
// response for an exhausted list. A total whose value is missing degrades to omitted.
export const parseCursorPageMeta = (headers: HeaderSource): CursorPageMeta => {
  const parsed = cursorHeadersSchema.parse({
    mode: readHeader(headers, 'x-pagination-mode'),
    nextCursor: readHeader(headers, 'x-next-cursor'),
    previousCursor: readHeader(headers, 'x-previous-cursor'),
    hasNext: readHeader(headers, 'x-has-next'),
    hasPrevious: readHeader(headers, 'x-has-previous'),
    totalKind: readHeader(headers, 'x-total-count-kind')
  });

  return {
    nextCursor: parsed.nextCursor,
    previousCursor: parsed.previousCursor,
    hasNext: parsed.hasNext,
    hasPrevious: parsed.hasPrevious,
    total: parseTotal(headers, parsed.totalKind)
  };
};

export const toCursorPage = <T>(items: T[], headers: HeaderSource): CursorPage<T> => ({
  items,
  ...parseCursorPageMeta(headers)
});

// Query parameters selecting cursor mode; merge them with the endpoint's own filters.
export const cursorRequestParams = ({
  boundary,
  limit
}: Pick<CursorPageRequest<unknown>, 'boundary' | 'limit'>): Record<string, string | number> => {
  if (boundary.kind === 'after') {
    return { pagination: 'cursor', limit, after: boundary.cursor };
  }
  if (boundary.kind === 'before') {
    return { pagination: 'cursor', limit, before: boundary.cursor };
  }
  return { pagination: 'cursor', limit };
};

// Validates an untyped boundary such as a React Query pageParam; undefined is the first page.
export const parseCursorBoundary = (value: unknown): CursorBoundary =>
  value === undefined ? FIRST_CURSOR_BOUNDARY : cursorBoundarySchema.parse(value);

const PAGINATION_ERROR_CODES = [
  'invalid_cursor',
  'invalid_pagination',
  'cursor_pagination_unavailable'
] as const;

export type PaginationErrorCode = (typeof PAGINATION_ERROR_CODES)[number];

const paginationErrorSchema = z.object({
  error: z.object({ code: z.enum(PAGINATION_ERROR_CODES) })
});

// Reads the `{ error: { code } }` body the server sends with HTTP 400 for a pagination error.
export const paginationErrorCode = (error: unknown): PaginationErrorCode | null => {
  const parsed = paginationErrorSchema.safeParse(error);
  return parsed.success ? parsed.data.error.code : null;
};

// Restart the traversal (first boundary) and offer a refresh.
export const isInvalidCursorError = (error: unknown): boolean =>
  paginationErrorCode(error) === 'invalid_cursor';

// Restart the list in numbered mode.
export const isCursorUnavailableError = (error: unknown): boolean =>
  paginationErrorCode(error) === 'cursor_pagination_unavailable';

// Fetches one cursor page from a v4 endpoint, keeping the endpoint's body root: `selectItems`
// picks the records out of the body. The AbortSignal cancels a superseded request.
export const fetchCursorPage = async <TBody, TItem>({
  path,
  params = {},
  request,
  selectItems,
  signal
}: {
  path: string;
  params?: Record<string, unknown>;
  request: Pick<CursorPageRequest<unknown>, 'boundary' | 'limit'>;
  selectItems: (body: TBody) => TItem[];
  signal?: AbortSignal;
}): Promise<CursorPage<TItem>> => {
  const url = buildApiUrl(path, { ...params, ...cursorRequestParams(request) });
  const response = await fetch(`${window.location.origin}${url}`, {
    headers: { 'Content-Type': 'application/json' },
    signal
  });
  if (!response.ok) {
    // An Error carrying the same keys as RecruitApiClient's thrown payload (`error`, `code`),
    // so paginationErrorCode reads either.
    throw Object.assign(
      new Error('Cursor page request failed'),
      await parseErrorResponse(response)
    );
  }
  const body = (await response.json()) as TBody;
  return toCursorPage(selectItems(body), response.headers);
};

// Flattens infinite-query pages, keeping the first occurrence of each ID: a live list can
// shift a row across a page boundary between requests.
export const flattenCursorPages = <T>(
  pages: readonly CursorPage<T>[],
  getId: (item: T) => string | number
): T[] => {
  const seen = new Set<string | number>();
  return pages
    .flatMap((page) => page.items)
    .filter((item) => {
      const id = getId(item);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
};
